import { useCallback, useEffect, useRef } from 'react'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useAppSelector from '../../../hooks/useAppSelector'
import {
  getEntriesUrlSelector,
  getS3FetchInProgress,
  setAddOrUpdateEntries,
  setEntriesUrlFetchInProgress,
  setS3FetchInProgress,
  updateS3AllTeamEntries,
  updateS3Entries,
  setDownloadPercentage,
  getS3EntriesUpdatedForOfflineUse,
} from '../../data/dataSlice'
import ChronoSse from './ChronoSse'
import { createLogger } from '../../../app/logger'
import { useEventId } from '../../../app/location'
import { getOnlineSelector } from '../../network/networkSlice'
import { clearSession, getKioskIdSelector } from '../../session/sessionSlice'
import { S3CheckinEntry } from '../../../app/types'
import unzip from '../../../utils/unzip'
import { setSnackbarMessages } from '../../snackbar/snackbarSlice'
import updateDownloadPercentage from '../../../utils/updateDownloadPercentage'
import { mergeEntryUpdates } from '../../../utils/mergeEntryUpdates'
import { useUnsyncedUpdates } from '../../data/hooks/useUnsyncedUpdates'
import { getSseMessageReceivedButEntriesUrlIsUndefined, setSseMessageReceivedButEntriesUrlIsUndefined } from '../sseSlice'
import useLoadLocalForageKioskChanges from '../../localForage/hooks/useLoadLocalForageKioskChanges'
import useUpdateLocalForageEntries from '../../localForage/hooks/useUpdateLocalForageEntries'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'CheckinSse'
})

export default function CheckinSse() {
  const dispatch = useAppDispatch()
  const online = useAppSelector(getOnlineSelector)
  const entriesUrl = useAppSelector(getEntriesUrlSelector)
  const s3FetchInProgress = useAppSelector(getS3FetchInProgress)
  const entriesUpdatedForOfflineUse = useAppSelector(getS3EntriesUpdatedForOfflineUse)
  const sseMessageReceivedButEntriesUrlIsUndefined = useAppSelector(getSseMessageReceivedButEntriesUrlIsUndefined)
  const kioskId = useAppSelector(getKioskIdSelector)
  const eventId = useEventId()
  const unsyncedUpdates = useUnsyncedUpdates()
  const wasOfflineRef = useRef(false)
  const loadLocalForageKioskChanges = useLoadLocalForageKioskChanges()
  const updateLocalForageEntries = useUpdateLocalForageEntries()

  const entryUpdatesMap = useRef<Map<number, S3CheckinEntry>>(new Map())

  const refetch = useCallback(async () => {
    if (!online || s3FetchInProgress) {
      return
    }
    if (!entriesUrl) {
      // for race condition where api publishes signal to download entries to sse before entriesUrl available
      dispatch(setSseMessageReceivedButEntriesUrlIsUndefined(true))
      return
    }
    else if (sseMessageReceivedButEntriesUrlIsUndefined) {
      dispatch(setSseMessageReceivedButEntriesUrlIsUndefined(false))
    }
    logger.debug('fetching data for url', { entriesUrl, time: new Date() })

    dispatch(setS3FetchInProgress(true))

    try {
      const response = await fetch(entriesUrl)
      const entriesUnzipped = await unzip(
        await updateDownloadPercentage(
          response,
          percentageProcessed => {
            logger.info(`Progress: ${percentageProcessed}%`)
            dispatch(setDownloadPercentage(percentageProcessed))
          }
        )
      )
      const parsedEntries = JSON.parse(new TextDecoder().decode(entriesUnzipped)) as S3CheckinEntry[]
      if (!parsedEntries.length) {
        return
      }

      const entries = unsyncedUpdates.length
        ? mergeEntryUpdates(parsedEntries, unsyncedUpdates)
        : parsedEntries

      dispatch(updateS3Entries(entries))
      dispatch(updateS3AllTeamEntries(entries.filter(({ team_id }) => team_id)))

      await loadLocalForageKioskChanges(eventId)
      await updateLocalForageEntries(eventId, entries)
    } catch (err) {
      dispatch(setSseMessageReceivedButEntriesUrlIsUndefined(false))
      logger.error(err)
      dispatch(setSnackbarMessages({
        messageLines: [
          {
            message: 'There was an error encountered while fetching entries.'
          }
        ],
        severity: 'error'
      }))
    }

    logger.debug('finished fetching data', { entriesUrl, time: new Date() })
    dispatch(setS3FetchInProgress(false))
    dispatch(setEntriesUrlFetchInProgress(false))
  }, [
    dispatch,
    entriesUrl,
    eventId,
    online,
    s3FetchInProgress,
    unsyncedUpdates,
    sseMessageReceivedButEntriesUrlIsUndefined,
    loadLocalForageKioskChanges,
    updateLocalForageEntries,
  ])

  useEffect(() => {
    // test removing sseMessageReceivedButEntriesUrlIsUndefined
    if (entriesUrl && sseMessageReceivedButEntriesUrlIsUndefined) {
      refetch()
    }
  }, [
    dispatch,
    entriesUrl,
    sseMessageReceivedButEntriesUrlIsUndefined,
    refetch,
  ])

  useEffect(() => {
    if (online && wasOfflineRef.current) {
      refetch()
    }
    wasOfflineRef.current = !online
  }, [online, refetch])

  useEffect(() => {
    const timer = setInterval(() => {
      if (entryUpdatesMap.current.size) {
        dispatch(setAddOrUpdateEntries(
          Array.from(entryUpdatesMap.current.values())
        ))
        entryUpdatesMap.current = new Map()
      }
    }, 60000)

    return () => clearInterval(timer)
  }, [
    dispatch,
    entryUpdatesMap,
  ])

  const onSseMessage = useCallback((message: any) => {
    const parsed = JSON.parse(message || '{}')
    const { type, entryUpdates } = parsed
    if (type === 'entriesUpdated' && kioskId !== parsed.kioskId) {
      if (entryUpdates) {
        for (const e of entryUpdates as S3CheckinEntry[]) {
          entryUpdatesMap.current.set(e.entry_id, e)
        }
      } else if (!entriesUpdatedForOfflineUse) {
        // if entryUpdates undefined then should refetch all entries
        refetch()
      }
    }

    if (type === 'accessCodeUpdated') {
      dispatch(clearSession())
    }
  }, [
    entryUpdatesMap,
    kioskId,
    entriesUpdatedForOfflineUse,
    refetch,
    dispatch,
  ])

  logger.debug('Sse', { online, eventId })

  if (!online) {
    return null
  }

  return (
    <ChronoSse
      onMessage={onSseMessage}
      path={`event/${eventId}/checkin`}
    />
  )
}
